import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode } from 'react';

interface PrivacySectionProps {
  title?: string;
  content: ReactNode | string[];
  isCompanyInfo?: boolean;
}

// Base card styles to avoid duplication
const BaseCard = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--overlay-very-light)",
  borderRadius: "40px",
  padding: "40px",
  width: "calc(100%)",
  maxWidth: "1200px",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: "24px",
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
    borderRadius: "16px",
    margin: "0 8px",
  },
}));

const SectionCard = styled(BaseCard)(({ theme }) => ({
  gap: theme.spacing(2), // 16px
}));

const CompanyInfoCard = styled(BaseCard)(() => ({
  // No gap for company info
}));

// Text components with shared styles
const TextBase = styled(Typography)(({ theme }) => ({
  color: "var(--text-primary)",
  ...theme.typography.body1,
  wordWrap: "break-word",
  overflowWrap: "break-word",
}));

const NoMarginText = styled(TextBase)(() => ({
  marginBottom: "0 !important",
}));

// Helper function for formatting text with bold text before a colon
const formatCompanyInfo = (text: string) => {
  const colonIndex = text.indexOf(":");
  if (colonIndex === -1) {
    return text;
  }

  const beforeColon = text.substring(0, colonIndex + 1);
  const afterColon = text.substring(colonIndex + 1);

  return (
    <>
      <strong style={{ fontWeight: 700 }}>{beforeColon}</strong>
      <span style={{ fontWeight: 400 }}>{afterColon}</span>
    </>
  );
};

const PrivacySection: React.FC<PrivacySectionProps> = ({ title, content, isCompanyInfo = false }) => {
  const CardComponent = isCompanyInfo ? CompanyInfoCard : SectionCard;

  const renderContent = () => {
    if (Array.isArray(content)) {
      return content.map((paragraph, index) => (
        <NoMarginText key={index} variant="body1">
          {isCompanyInfo ? formatCompanyInfo(paragraph) : paragraph}
        </NoMarginText>
      ));
    }

    return (
      <NoMarginText variant="body1">
        {content}
      </NoMarginText>
    );
  };

  return (
    <CardComponent>
      {title && (
        <Typography variant="h4" component="h2">
          {title}
        </Typography>
      )}
      {renderContent()}
    </CardComponent>
  );
};

export default PrivacySection;